import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo } from "react";
import "./style.css";
import {
  calculateAminoAcidFrequency,
  aminoAcidShortNames
} from "./calculateAminoAcidFrequency";
import { Button } from "@blueprintjs/core";
export default ({ properties, setProperties, style }) => {
  const sidebarRef = React.useRef(null);
  const [mismatchesCount, setMismatchesCount] = React.useState(0);
  const [mismatchesInRange, setMismatchesInRange] = React.useState(0);
  const { track, isOpen, selection, isPairwise } = properties;
  const getSequenceInRegion = useCallback(() => {
    const seq = track?.alignmentData?.sequence ?? "";
    if (!selection || selection.start === -1 || selection.end === -1) {
      return seq;
    }
    const start = Math.max(0, selection.start);
    const end = Math.min(seq.length - 1, selection.end);
    if (start > end) return "";
    return seq.slice(start, end + 1);
  }, [track, selection]);
  const mismatchKey = isPairwise ? "additionalSelectionLayers" : "mismatches";
  const trackMismatches = useMemo(() => {
    const tr = track?.[mismatchKey];
    if (!Array.isArray(tr)) return [];
    return isPairwise ? tr.filter((m) => m?.color === "red") : tr;
  }, [track, mismatchKey, isPairwise]);
  useEffect(() => {
    if (!isOpen || sidebarRef.current === null || !track) {
      return;
    }
    sidebarRef.current.focus();
    let mismatchCount = 0;
    trackMismatches?.forEach((tm) => {
      if (tm === null || tm.start === null || tm.end === null) {
        return;
      }
      const overlapStart = tm.start;
      const overlapEnd = tm.end;
      if (overlapEnd >= overlapStart) {
        mismatchCount += overlapEnd - overlapStart + 1;
      }
    });
    setMismatchesCount(mismatchCount);
    setMismatchesInRange(mismatchCount);
    if (selection && selection.start > -1 && selection.end > -1) {
      let count = 0;
      trackMismatches?.forEach((tm) => {
        if (tm === null || tm.start === null || tm.end === null) {
          return;
        }
        const overlapStart = Math.max(tm.start, selection.start);
        const overlapEnd = Math.min(tm.end, selection.end);
        if (overlapEnd >= overlapStart) {
          count += overlapEnd - overlapStart + 1;
        }
      });
      setMismatchesInRange(count);
    }
  }, [isOpen, track, selection, trackMismatches]);
  const aminoFreq = useMemo(() => {
    const seq = getSequenceInRegion();
    return calculateAminoAcidFrequency(
      seq,
      track?.sequenceData?.isProtein ?? false
    );
  }, [getSequenceInRegion, track]);
  if (!isOpen) {
    return null;
  }
  let trackInner;
  if (track) {
    const {
      name,
      isProtein,
      proteinSize,
      size,
      molecularWeight,
      isoPoint,
      extinctionCoefficient
    } = track.sequenceData;
    const frequencyEntries = Object.entries(aminoFreq.frequencies);
    trackInner = /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            display: "flex",
            padding: 4,
            paddingTop: 11,
            paddingBottom: 11,
            width: "100%"
          }
        }
      ),
      /* @__PURE__ */ jsx("h5", { children: "Track Properties" }),
      /* @__PURE__ */ jsxs("div", { className: "bp3-tab-panel", children: [
        /* @__PURE__ */ jsx(RowItem, { item: name, title: "Name" }),
        /* @__PURE__ */ jsx(RowItem, { item: isProtein ? proteinSize : size, title: "Length" }),
        /* @__PURE__ */ jsx(
          RowItem,
          {
            item: molecularWeight?.toFixed(2),
            title: "Molecular Weight",
            units: isProtein ? "Da" : "g/mol"
          }
        ),
        name !== "Consensus" && isProtein && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(RowItem, { item: isoPoint, title: "Isoelectric Point" }),
          /* @__PURE__ */ jsx(
            RowItem,
            {
              item: extinctionCoefficient,
              title: "Extinction Coefficient"
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          RowItem,
          {
            item: `${mismatchesInRange}/${mismatchesCount}`,
            title: "Mismatches"
          }
        ),
        /* @__PURE__ */ jsx(
          RowItem,
          {
            item: selection && selection.start > -1 ? /* @__PURE__ */ jsxs("span", { children: [
              selection.start + 1,
              " - ",
              selection.end + 1
            ] }) : /* @__PURE__ */ jsxs("span", { children: [
              "1 - ",
              isProtein ? proteinSize : size
            ] }),
            title: "Region"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("h5", { children: [
        isProtein ? "Amino Acid" : "Base Pair",
        " Frequencies"
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "sidebar-table", children: [
        /* @__PURE__ */ jsxs("div", { className: "sidebar-row", children: [
          /* @__PURE__ */ jsx("div", { className: "sidebar-cell", children: isProtein ? "Amino Acid" : "Base" }),
          /* @__PURE__ */ jsx("div", { className: "sidebar-cell", children: "Count" }),
          /* @__PURE__ */ jsx("div", { className: "sidebar-cell", children: "Percentage" })
        ] }),
        frequencyEntries.map(([aa, data], idx) => {
          return /* @__PURE__ */ jsxs("div", { className: `sidebar-row property-amino-acid-${idx}`, children: [
            /* @__PURE__ */ jsxs("div", { className: "sidebar-cell", children: [
              aa,
              " ",
              isProtein ? `(${aminoAcidShortNames[aa]})` : ""
            ] }),
            /* @__PURE__ */ jsx("div", { className: "sidebar-cell", children: data.count }),
            /* @__PURE__ */ jsxs("div", { className: "sidebar-cell", children: [
              data.percentage.toFixed(1),
              "%"
            ] })
          ] });
        })
      ] })
    ] });
  } else {
    trackInner = /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          marginTop: 20,
          fontStyle: "italic",
          fontSize: 16
        },
        children: "Click on a track to view its properties"
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: sidebarRef,
      style: {
        position: "relative",
        width: isOpen ? 350 : 0,
        minWidth: isOpen ? 350 : 0,
        maxWidth: isOpen ? 350 : 0,
        paddingLeft: 20,
        paddingRight: 20,
        ...style
      },
      className: "ove-sidebar-container",
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.key === "Escape") {
          setProperties({ isOpen: false });
        }
      },
      children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            style: {
              position: "absolute",
              top: 0,
              right: 10,
              zIndex: 1,
              cursor: "pointer"
            },
            minimal: true,
            intent: "primary",
            "data-tip": "Hide Track Properties",
            icon: "cross",
            onClick: () => setProperties({ isOpen: false })
          }
        ),
        trackInner
      ]
    }
  );
};
function RowItem({ item, title, units }) {
  if (!item) return;
  const propertyClass = title.split(" ").join("-").toLowerCase();
  return /* @__PURE__ */ jsxs("div", { className: `ve-flex-row property-${propertyClass}`, children: [
    /* @__PURE__ */ jsx("div", { className: "ve-column-left", children: title }),
    /* @__PURE__ */ jsxs("div", { className: "ve-column-right", children: [
      item,
      " ",
      units ?? ""
    ] })
  ] });
}
